import { Trophy, TrendingDown, Minus, Clock, PlayCircle, CircleSlash } from "lucide-react";
import { cn } from "@/lib/utils";

export type LegResult = "pending" | "won" | "lost" | "pushed" | "void";
export type LegLiveStatus = LegResult | "live";

/** Derive the visual status for a leg. */
export function deriveLegStatus(opts: {
  result?: string | null;
  commence_time?: string | null;
}): LegLiveStatus {
  const r = (opts.result ?? "pending") as LegResult;
  if (r !== "pending") return r;
  if (!opts.commence_time) return "pending";
  const t = new Date(opts.commence_time);
  if (isNaN(t.getTime())) return "pending";
  return t.getTime() <= Date.now() ? "live" : "pending";
}

const META: Record<
  LegLiveStatus,
  { label: string; cls: string; icon: typeof Trophy; pulse?: boolean }
> = {
  won:     { label: "Won",     cls: "border-success/40 bg-success/15 text-success shadow-[0_0_0_1px_hsl(var(--success)/0.15)]", icon: Trophy },
  lost:    { label: "Lost",    cls: "border-danger/40 bg-danger/15 text-danger shadow-[0_0_0_1px_hsl(var(--danger)/0.15)]", icon: TrendingDown },
  pushed:  { label: "Push",    cls: "border-warning/40 bg-warning/15 text-warning shadow-[0_0_0_1px_hsl(var(--warning)/0.15)]", icon: Minus },
  live:    { label: "Live",    cls: "border-primary/40 bg-primary/15 text-primary shadow-[0_0_0_1px_hsl(var(--primary)/0.15)]", icon: PlayCircle, pulse: true },
  pending: { label: "Pending", cls: "border-border bg-secondary/40 text-muted-foreground", icon: Clock },
  void:    { label: "Void",    cls: "border-muted-foreground/30 bg-muted/20 text-muted-foreground", icon: CircleSlash },
};

interface Props {
  result?: string | null;
  commence_time?: string | null;
  size?: "xs" | "sm";
  className?: string;
}

export default function LegStatusBadge({ result, commence_time, size = "sm", className }: Props) {
  const status = deriveLegStatus({ result, commence_time });
  const m = META[status];
  const Icon = m.icon;
  const sizeCls = size === "xs"
    ? "px-1.5 py-0.5 text-[10px] gap-1"
    : "px-2 py-0.5 text-[11px] gap-1.5";
  const iconCls = size === "xs" ? "h-2.5 w-2.5" : "h-3 w-3";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border font-bold uppercase tracking-wider",
        sizeCls,
        m.cls,
        className,
      )}
    >
      <span className="relative flex items-center justify-center">
        <Icon className={iconCls} />
        {m.pulse && (
          <span className="absolute inline-flex h-full w-full rounded-full bg-primary/40 opacity-75 animate-ping" />
        )}
      </span>
      {m.label}
    </span>
  );
}

/** Aggregate progress across legs. */
export function summarizeLegs(
  legs: Array<{ result?: string | null; commence_time?: string | null }>,
) {
  let won = 0, lost = 0, pushed = 0, live = 0, pending = 0;
  for (const l of legs) {
    const s = deriveLegStatus(l);
    if (s === "won") won++;
    else if (s === "lost") lost++;
    else if (s === "pushed") pushed++;
    else if (s === "live") live++;
    else pending++;
  }
  const settled = won + lost + pushed;
  return {
    total: legs.length,
    won, lost, pushed, live, pending,
    settled,
    anyLive: live > 0,
    allSettled: settled === legs.length && legs.length > 0,
  };
}
